import { MappedRuleType } from 'src/clientConfiguration/types';
import { KameleoonVisitorDataType } from 'src/kameleoonData/types';
import { FeatureFlagVariationsType } from 'src/storage/types';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { ConditionDataType, ConditionType } from './conditions';
import { Node } from './node';
import { Tree } from './tree';

export enum Operator {
  And = 'AND',
  Or = 'OR',
}

export type NonLeafNodeType = {
  left: Node;
  right: Node;
  value: Operator;
};

export type LeafNodeType = {
  left: undefined;
  right: undefined;
  value: ConditionType;
};

export type NodeValueType = ConditionType | Operator;

export type ConditionLevelType = {
  orOperators: boolean[];
  conditions: ConditionDataType[];
};

export type SegmentType = {
  id: string;
  name: string;
  audienceTracking?: boolean;
  conditionsData: {
    firstLevelOrOperators: boolean[];
    firstLevel: ConditionLevelType[];
  };
};

export type NestedConditionGroupType = {
  operators: Operator[];
  conditions: ConditionDataType[];
};

export type GroupedSegmentsType = {
  topLevelOperators: Operator[];
  nestedConditions: NestedConditionGroupType[];
};

export type VisitorReference = string;

export type TargetingDataType = {
  [visitorCode: string]: KameleoonVisitorDataType | VisitorReference;
};

export type DereferencedTargetingDataType = {
  [visitorCode: string]: KameleoonVisitorDataType;
};

export type EvaluationDataType = {
  targetingData?: KameleoonVisitorDataType;
  variationData: FeatureFlagVariationsType;
  ruleMap?: Map<number, MappedRuleType>;
  sdkInfo: ExternalPackageInfoType;
  experimentId: number | null;
  visitorCode: string;
  segments?: Map<string, SegmentType>;
  trees?: Map<string, Tree>;
};

export enum ValueMatchType {
  TRUE = 'TRUE',
  FALSE = 'FALSE',
  EXACT = 'EXACT',
  CONTAINS = 'CONTAINS',
  REGULAR_EXPRESSION = 'REGULAR_EXPRESSION',
  EQUAL = 'EQUAL',
  LOWER = 'LOWER',
  GREATER = 'GREATER',
  UNDEFINED = 'UNDEFINED',
  AMONG_VALUES = 'AMONG_VALUES',
  RANGE = 'RANGE',
}

export enum VisitorType {
  NEW = 'NEW',
  RETURNING = 'RETURNING',
}

export enum MatchTypeVariation {
  ANY = 'ANY',
  EXACT = 'EXACT',
}

export enum MatchTypeTest {
  ALL = 'ALL',
  TEST = 'TEST',
  PERSO = 'PERSO',
}

export enum CampaignType {
  Experiment = 'EXPERIMENT',
  Personalization = 'PERSONALIZATION',
  Any = 'ANY',
}

// --- Note ---
// Starting the merge of `matchType` with the release of new conditions
// Now it contains a couple of sub types, in future API version all match types
// will be accumulated in this enum
export enum MatchType {
  // -- Sub Type One
  EXACT = 'EXACT',
  CONTAINS = 'CONTAINS',
  REGULAR_EXPRESSION = 'REGULAR_EXPRESSION',
  // -- Sub Type Two
  GREATER = 'GREATER',
  EQUAL = 'EQUAL',
  LOWER = 'LOWER',
}

export enum VersionMatchType {
  GREATER = 'GREATER',
  EQUAL = 'EQUAL',
  LOWER = 'LOWER',
}

export enum NameMatchType {
  EXACT = 'EXACT',
  CONTAINS = 'CONTAINS',
  REGULAR_EXPRESSION = 'REGULAR_EXPRESSION',
  OPTIONAL = 'OPTIONAL',
}

export enum TargetingType {
  PAGE_URL = 'PAGE_URL',
  PAGE_TITLE = 'PAGE_TITLE',
  LANDING_PAGE = 'LANDING_PAGE',
  ORIGIN = 'ORIGIN',
  ORIGIN_TYPE = 'ORIGIN_TYPE',
  REFERRERS = 'REFERRERS',
  NEW_VISITORS = 'NEW_VISITORS',
  INTERESTS = 'INTERESTS',
  BROWSER_LANGUAGE = 'BROWSER_LANGUAGE',
  GEOLOCATION = 'GEOLOCATION',
  DEVICE_TYPE = 'DEVICE_TYPE',
  SCREEN_DIMENSION = 'SCREEN_DIMENSION',
  VISITOR_IP = 'VISITOR_IP',
  AD_BLOCKER = 'AD_BLOCKER',
  PREVIOUS_PAGE = 'PREVIOUS_PAGE',
  KEY_PAGES = 'KEY_PAGES',
  PAGE_VIEWS = 'PAGE_VIEWS',
  FIRST_VISIT = 'FIRST_VISIT',
  LAST_VISIT = 'LAST_VISIT',
  ACTIVE_SESSION = 'ACTIVE_SESSION',
  TIME_SINCE_PAGE_LOAD = 'TIME_SINCE_PAGE_LOAD',
  SAME_DAY_VISITS = 'SAME_DAY_VISITS',
  VISITS = 'VISITS',
  VISITS_BY_PAGE = 'VISITS_BY_PAGE',
  VISITOR_CODE = 'VISITOR_CODE',
  SDK_LANGUAGE = 'SDK_LANGUAGE',
  INTERNAL_SEARCH_KEYWORDS = 'INTERNAL_SEARCH_KEYWORDS',
  TABS_ON_SITE = 'TABS_ON_SITE',
  CONVERSION_PROBABILITY = 'CONVERSION_PROBABILITY',
  HEAT_SLICE = 'HEAT_SLICE',
  SKY_STATUS = 'SKY_STATUS',
  TEMPERATURE = 'TEMPERATURE',
  DAY_NIGHT = 'DAY_NIGHT',
  FORECAST_SKY_STATUS = 'FORECAST_SKY_STATUS',
  FORECAST_TEMPERATURE = 'FORECAST_TEMPERATURE',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  TIME_RANGE = 'TIME_RANGE',
  HOUR_MINUTE_RANGE = 'HOUR_MINUTE_RANGE',
  JS_CODE = 'JS_CODE',
  COOKIE = 'COOKIE',
  EVENT = 'EVENT',
  BROWSER = 'BROWSER',
  OPERATING_SYSTEM = 'OPERATING_SYSTEM',
  DOM_ELEMENT = 'DOM_ELEMENT',
  MOUSE_OUT = 'MOUSE_OUT',
  TARGET_EXPERIMENT = 'TARGET_EXPERIMENT',
  TARGET_FEATURE_FLAG = 'TARGET_FEATURE_FLAG',
  TARGET_PERSONALIZATION = 'TARGET_PERSONALIZATION',
  EXCLUSIVE_EXPERIMENT = 'EXCLUSIVE_EXPERIMENT',
  CONVERSIONS = 'CONVERSIONS',
  CUSTOM_DATUM = 'CUSTOM_DATUM',
  YSANCE_SEGMENT = 'YSANCE_SEGMENT',
  SEGMENT = 'SEGMENT',
  YSANCE_ATTRIBUT = 'YSANCE_ATTRIBUT',
  TEALIUM_BADGE = 'TEALIUM_BADGE',
  TEALIUM_AUDIENCE = 'TEALIUM_AUDIENCE',
  PRICE_OF_DISPLAYED_PAGE = 'PRICE_OF_DISPLAYED_PAGE',
  NUMBER_OF_VISITED_PAGES = 'NUMBER_OF_VISITED_PAGES',
  VISITED_PAGES = 'VISITED_PAGES',
  CUSTOM = 'CUSTOM',
  EXPLICIT_TRIGGER = 'EXPLICIT_TRIGGER',
  APPLICATION_VERSION = 'APPLICATION_VERSION',
}
